import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { DollarSign, Save, RefreshCw, TrendingUp, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPricing = () => {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  const token = localStorage.getItem('mojo_admin_token');

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/pricing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPricing(response.data.pricing);
      initializeEditValues(response.data.pricing);
    } catch (error) {
      toast.error('Failed to load pricing settings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const initializeEditValues = (pricingData) => {
    const values = {};
    Object.entries(pricingData).forEach(([category, services]) => {
      if (category === 'config_type' || category === 'updated_at' || category === 'updated_by') return;
      
      Object.entries(services).forEach(([service, data]) => {
        Object.entries(data).forEach(([field, value]) => {
          if (typeof value === 'number' || typeof value === 'boolean') {
            const key = `${category}.${service}.${field}`;
            values[key] = value;
          }
        });
      });
    });
    setEditValues(values);
  };

  const handleChange = (category, service, field, value) => {
    const key = `${category}.${service}.${field}`;
    setEditValues(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const saveAllChanges = async () => {
    setSaving(true);
    try {
      // Build nested update object
      const updates = {};
      
      Object.entries(editValues).forEach(([key, value]) => {
        const [category, service, field] = key.split('.');
        
        if (!updates[category]) updates[category] = {};
        if (!updates[category][service]) updates[category][service] = {};
        updates[category][service][field] = value;
      });
      
      await axios.put(
        `${API}/admin/pricing`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Pricing settings updated successfully!');
      setHasChanges(false);
      fetchPricing();
    } catch (error) {
      toast.error('Failed to update pricing settings');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const PricingField = ({ category, service, field, value, label, type = 'number', suffix = '' }) => {
    const key = `${category}.${service}.${field}`;
    const editValue = editValues[key];

    if (type === 'boolean') {
      return (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <Label className="text-sm font-medium text-gray-700">{label}</Label>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={editValue}
              onChange={(e) => handleChange(category, service, field, e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#F2B10C]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#145539]"></div>
          </label>
        </div>
      );
    }

    return (
      <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
        <Label className="text-sm font-medium text-gray-700">{label}</Label>
        <div className="flex gap-2 items-center">
          <Input
            type="number"
            step="0.01"
            min="0"
            value={editValue ?? value}
            onChange={(e) => handleChange(category, service, field, parseFloat(e.target.value) || 0)}
            className="flex-1"
          />
          {suffix && <span className="text-sm font-medium text-gray-600 min-w-fit">{suffix}</span>}
        </div>
      </div>
    );
  };

  const ServiceCard = ({ category, service, data }) => {
    const displayName = service.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    
    return (
      <Card className="mb-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{displayName}</CardTitle>
            {data.enabled !== undefined && (
              <span className={`px-2 py-1 text-xs rounded ${data.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                {data.enabled ? 'Active' : 'Inactive'}
              </span>
            )}
          </div>
          {data.note && (
            <CardDescription className="text-sm">{data.note}</CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(data).map(([field, value]) => {
              if (field === 'note' || field === 'mode') return null;
              
              const fieldConfig = {
                fee_percentage: { label: 'Fee Percentage', suffix: '%' },
                min_fee: { label: 'Minimum Fee', suffix: 'ZAR' },
                max_fee: { label: 'Maximum Fee', suffix: 'ZAR' },
                platform_fee: { label: 'Platform Fee', suffix: 'ZAR' },
                merchant_commission_percentage: { label: 'Merchant Commission', suffix: '%' },
                platform_commission_percentage: { label: 'Platform Commission', suffix: '%' },
                platform_fee_percentage: { label: 'Platform Fee', suffix: '%' },
                commission_percentage: { label: 'Commission', suffix: '%' },
                collection_fee_percentage: { label: 'Collection Fee', suffix: '%' },
                current_value_zar: { label: 'Current Value', suffix: 'ZAR' },
                base_value_zar: { label: 'Base Value', suffix: 'ZAR' },
                discount_percentage: { label: 'Discount', suffix: '%' },
                buy_fee_percentage: { label: 'Buy Fee', suffix: '%' },
                redeem_fee_percentage: { label: 'Redeem Fee', suffix: '%' },
                transfer_fee_percentage: { label: 'Transfer Fee', suffix: '%' },
                max_transfer_fee: { label: 'Max Transfer Fee', suffix: 'ZAR' },
                daily_limit: { label: 'Daily Limit', suffix: 'ZAR' },
                monthly_limit: { label: 'Monthly Limit', suffix: 'ZAR' },
                withdrawal_fee: { label: 'Withdrawal Fee', suffix: 'ZAR' },
                deposit_fee: { label: 'Deposit Fee', suffix: 'ZAR' },
                enabled: { label: 'Enabled', type: 'boolean' }
              };
              
              const config = fieldConfig[field] || { label: field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) };
              
              return (
                <PricingField
                  key={field}
                  category={category}
                  service={service}
                  field={field}
                  value={value}
                  label={config.label}
                  type={config.type || 'number'}
                  suffix={config.suffix || ''}
                />
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin mx-auto text-[#F2B10C] mb-4" />
          <p className="text-gray-600">Loading pricing settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-[#145539]" />
            <h1 className="text-3xl font-bold text-[#1B0C34]">Pricing & Costing Settings</h1>
          </div>
          
          {hasChanges && (
            <Button
              onClick={saveAllChanges}
              disabled={saving}
              className="bg-gradient-to-r from-[#145539] to-[#1e7049]"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save All Changes
                </>
              )}
            </Button>
          )}
        </div>
        
        <p className="text-gray-600">Manage fees, commissions, and pricing across all services</p>
        
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-blue-800 font-medium">Important</p>
              <p className="text-xs text-blue-700 mt-1">
                Pricing changes apply immediately to new transactions. All changes are logged for audit purposes.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing by Category */}
      {pricing && Object.entries(pricing).map(([category, services]) => {
        if (category === 'config_type' || category === 'updated_at' || category === 'updated_by') return null;
        
        return (
          <div key={category} className="mb-8">
            <h2 className="text-2xl font-bold text-[#1B0C34] mb-4 capitalize flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-[#F2B10C]" />
              {category.replace(/_/g, ' ')}
            </h2>
            
            {Object.entries(services).map(([service, data]) => (
              <ServiceCard
                key={service}
                category={category}
                service={service}
                data={data}
              />
            ))}
          </div>
        );
      })}

      {/* Sticky Save Button */}
      {hasChanges && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button
            onClick={saveAllChanges}
            disabled={saving}
            size="lg"
            className="bg-gradient-to-r from-[#145539] to-[#1e7049] shadow-lg"
          >
            {saving ? (
              <>
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save All Changes
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminPricing;
